import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, Printer, FileText, FileSpreadsheet, ChevronDown, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import DocumentPreview from '@/components/DocumentPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ExportButton - Reusable export/print component
 * 
 * Props:
 * - condominiumId: string - Required for API calls
 * - exportType: string - Type of export (audit-log, vat-report, etc.)
 * - title: string - Display title for the button/dialog
 * - formats: array - Available formats ['pdf', 'csv', 'print'] (default: all)
 * - filters: object - Current filters to pass to export
 * - showDateRange: boolean - Show date range picker
 * - showArchive: boolean - Show archive to documents option
 * - additionalParams: object - Additional query params
 * - onExportComplete: function - Callback after export
 * - variant: string - Button variant (default, outline, ghost)
 * - size: string - Button size (default, sm, lg)
 * - token: string - Auth token
 * - customEndpoint: string - Custom endpoint path (optional, overrides default)
 */
export default function ExportButton({
  condominiumId,
  exportType,
  title = 'Esporta',
  formats = ['pdf', 'csv', 'print'],
  filters = {},
  showDateRange = false,
  showArchive = false,
  additionalParams = {},
  onExportComplete,
  variant = 'outline',
  size = 'default',
  token,
  className = '',
  customEndpoint = null,
}) {
  const [showDialog, setShowDialog] = useState(false);
  // anteprima: si guarda il documento prima di decidere se salvarlo o stamparlo
  const [anteprima, setAnteprima] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [archiveToDocuments, setArchiveToDocuments] = useState(false);

  // Build query string from filters and params
  const buildQueryString = (format) => {
    const params = new URLSearchParams();
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    
    // Add additional params
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    
    // Add date range if applicable
    if (showDateRange) {
      if (dateFrom) params.append('from_date', dateFrom);
      if (dateTo) params.append('to_date', dateTo);
    }
    
    // Add archive option for PDF
    if (format === 'pdf' && archiveToDocuments) {
      params.append('archive', 'true');
    }
    
    return params.toString();
  };

  // Handle PDF export
  const handlePdfExport = async () => {
    setLoading(true);
    try {
      const queryString = buildQueryString('pdf');
      // Use custom endpoint or default
      const url = customEndpoint 
        ? `${API_URL}${customEndpoint}.pdf${queryString ? `?${queryString}` : ''}`
        : `${API_URL}/api/exports/${condominiumId}/${exportType}.pdf${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Errore durante export');
      }
      
      // Check for archived document ID
      const documentId = response.headers.get('X-Document-Id');
      
      // Download the file
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${exportType}_${condominiumId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('PDF esportato con successo');
      
      if (documentId) {
        toast.info('PDF archiviato nei Documenti (M4)', {
          description: `ID: ${documentId.slice(0, 8)}...`
        });
      } else if (archiveToDocuments) {
        toast.info('PDF archiviato nei Documenti');
      }
      
      if (onExportComplete) onExportComplete('pdf', documentId);
      setShowDialog(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Errore durante l\'export PDF');
    } finally {
      setLoading(false);
    }
  };

  // Handle CSV export
  const handleCsvExport = async () => {
    setLoading(true);
    try {
      const queryString = buildQueryString('csv');
      // Use custom endpoint or default
      const url = customEndpoint 
        ? `${API_URL}${customEndpoint}.csv${queryString ? `?${queryString}` : ''}`
        : `${API_URL}/api/exports/${condominiumId}/${exportType}.csv${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Errore durante export');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${exportType}_${condominiumId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('CSV esportato con successo');
      
      if (onExportComplete) onExportComplete('csv');
      setShowDialog(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Errore durante l\'export CSV');
    } finally {
      setLoading(false);
    }
  };

  // Apre l'anteprima del documento senza salvarlo
  const apriAnteprima = (format) => {
    const queryString = buildQueryString(format);
    const url = customEndpoint
      ? `${API_URL}${customEndpoint}.${format}${queryString ? `?${queryString}` : ''}`
      : `${API_URL}/api/exports/${condominiumId}/${exportType}.${format}${queryString ? `?${queryString}` : ''}`;
    setAnteprima({
      url,
      formato: format,
      nomeFile: `${exportType}_${condominiumId}.${format}`,
      titolo: `${title} — anteprima`,
    });
    setShowDialog(false);
  };

  // Handle print (opens browser print dialog)
  const handlePrint = () => {
    window.print();
    toast.info('Usa la finestra di stampa del browser');
  };

  // Quick export (no dialog)
  const handleQuickExport = (format) => {
    if (format === 'pdf') {
      if (showDateRange || showArchive) {
        setShowDialog(true);
      } else {
        handlePdfExport();
      }
    } else if (format === 'csv') {
      handleCsvExport();
    } else if (format === 'print') {
      handlePrint();
    }
  };

  // Single format - show simple button
  if (formats.length === 1) {
    const format = formats[0];
    const icon = format === 'pdf' ? <FileText className="h-4 w-4 mr-2" /> :
                 format === 'csv' ? <FileSpreadsheet className="h-4 w-4 mr-2" /> :
                 <Printer className="h-4 w-4 mr-2" />;
    const label = format === 'pdf' ? 'Esporta PDF' :
                  format === 'csv' ? 'Esporta CSV' :
                  'Stampa';
    
    return (
      <>
        <Button
          variant={variant}
          size={size}
          onClick={() => (format === 'print' ? handlePrint() : apriAnteprima(format))}
          disabled={loading}
          className={className}
          title="Apre l'anteprima: salvare o stampare resta una tua scelta"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : icon}
          {label}
        </Button>
        <DocumentPreview
          open={!!anteprima}
          onOpenChange={(v) => !v && setAnteprima(null)}
          url={anteprima?.url}
          token={token}
          titolo={anteprima?.titolo}
          nomeFile={anteprima?.nomeFile}
          formato={anteprima?.formato}
        />
      </>
    );
  }

  // Multiple formats - show dropdown
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} size={size} className={className}>
            <Download className="h-4 w-4 mr-2" />
            {title}
            <ChevronDown className="h-4 w-4 ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {formats.includes('pdf') && (
            <DropdownMenuItem onClick={() => apriAnteprima('pdf')}>
              <Eye className="h-4 w-4 mr-2" />
              Anteprima PDF
            </DropdownMenuItem>
          )}

          {formats.includes('csv') && (
            <DropdownMenuItem onClick={() => apriAnteprima('csv')}>
              <Eye className="h-4 w-4 mr-2" />
              Anteprima dati
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {formats.includes('pdf') && (
            <DropdownMenuItem onClick={() => handleQuickExport('pdf')}>
              <FileText className="h-4 w-4 mr-2" />
              Scarica PDF
            </DropdownMenuItem>
          )}

          {formats.includes('csv') && (
            <DropdownMenuItem onClick={() => handleQuickExport('csv')}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Scarica CSV
            </DropdownMenuItem>
          )}

          {formats.includes('print') && <DropdownMenuSeparator />}
          {formats.includes('print') && (
            <DropdownMenuItem onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Stampa la pagina
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <DocumentPreview
        open={!!anteprima}
        onOpenChange={(v) => !v && setAnteprima(null)}
        url={anteprima?.url}
        token={token}
        titolo={anteprima?.titolo}
        nomeFile={anteprima?.nomeFile}
        formato={anteprima?.formato}
      />

      {/* Options Dialog for PDF with date range/archive */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Opzioni Export PDF</DialogTitle>
            <DialogDescription>
              Configura le opzioni per l&apos;export
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {showDateRange && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dateFrom">Da</Label>
                  <Input
                    id="dateFrom"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dateTo">A</Label>
                  <Input
                    id="dateTo"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
              </div>
            )}
            
            {showArchive && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="archive"
                  checked={archiveToDocuments}
                  onCheckedChange={setArchiveToDocuments}
                />
                <Label htmlFor="archive" className="text-sm font-normal cursor-pointer">
                  Archivia PDF nei Documenti (M4)
                </Label>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handlePdfExport} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Esportazione...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Esporta PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * PrintArea - Wrapper component for printable content
 * 
 * Usage:
 * <PrintArea id="print-content" title="Report Title">
 *   <YourContent />
 * </PrintArea>
 */
export function PrintArea({ children, id = 'print-area', title, className = '' }) {
  return (
    <div id={id} className={`print:block ${className}`}>
      {title && (
        <div className="hidden print:block print:mb-4">
          <h1 className="text-xl font-bold">{title}</h1>
          <p className="text-sm text-gray-500">
            Stampato il {new Date().toLocaleDateString('it-IT')}
          </p>
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * useExport - Hook for programmatic exports
 */
export function useExport(token) {
  const exportToPdf = async (condominiumId, exportType, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_URL}/api/exports/${condominiumId}/${exportType}.pdf${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (!response.ok) {
      throw new Error('Export failed');
    }
    
    const blob = await response.blob();
    return blob;
  };

  const exportToCsv = async (condominiumId, exportType, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `${API_URL}/api/exports/${condominiumId}/${exportType}.csv${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (!response.ok) {
      throw new Error('Export failed');
    }
    
    const blob = await response.blob();
    return blob;
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return { exportToPdf, exportToCsv, downloadBlob };
}
